import {produce} from 'immer';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {DataParameterTypedValue, ProcedureDataParameterSetting} from 'procedures/ProcedureDomainTypes';
import {
  DataParameterFieldType,
  DataParameterType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {
  doesFieldTypeSupportMinMax,
  makeDataParameterTypedValueFromText,
} from 'procedures/tables/procedureTableAdapterUtils';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

/**
 * Merges a library-derived field type with the existing setting's data type.
 * The library definition dictates the field type (FLOAT/TEXT/DATETIME), but the unit is configured
 * independently in the table and must be preserved.
 */
function mergeLibraryFieldTypeWithExistingUnit(
  libraryFieldType: DataParameterFieldType,
  existingDataType: DataParameterType
): DataParameterType {
  if (libraryFieldType === FieldType.FLOAT) {
    return {
      type: FieldType.FLOAT,
      requiredLink: null,
      unit: existingDataType.type === FieldType.FLOAT ? existingDataType.unit : null,
    };
  }
  return {type: libraryFieldType, requiredLink: null, unit: null};
}

/**
 * Converts a setpoint typed value to match the target field type.
 * Preserves the display text across type changes.
 */
function convertSetpointTypedValue(
  existing: DataParameterTypedValue,
  targetType: DataParameterFieldType
): DataParameterTypedValue {
  if (existing.type === targetType) {
    return existing;
  }
  return makeDataParameterTypedValueFromText(targetType, existing.value);
}

/**
 * Reconciles type-dependent fields (minimum, maximum, setpoint value) when the
 * resolved data type differs from what buildCoreDataParameterSetting inferred
 * from the (potentially stale) TYPE cell.
 */
function reconcileTypeDependentFields(
  setting: ProcedureDataParameterSetting,
  newDataType: DataParameterType
): ProcedureDataParameterSetting {
  const supportsMinMax = doesFieldTypeSupportMinMax(newDataType.type);
  const {minimum, maximum} = setting.dataParameterVersion;
  const hasMinMax = minimum != null || maximum != null;
  const needsMinMaxClear =
    hasMinMax &&
    (!supportsMinMax ||
      (minimum != null && minimum.type !== newDataType.type) ||
      (maximum != null && maximum.type !== newDataType.type));

  const plannedValue = setting.values[0];
  assert(setting.values.length === 1, 'Expected only one setpoint value');
  const needsSetpointConvert = plannedValue != null && plannedValue.value.type !== newDataType.type;

  if (!needsMinMaxClear && !needsSetpointConvert) {
    return setting;
  }

  return {
    ...setting,
    dataParameterVersion: {
      ...setting.dataParameterVersion,
      ...(needsMinMaxClear ? {minimum: null, maximum: null} : {}),
    },
    ...(needsSetpointConvert
      ? {
          values: [
            {
              ...plannedValue,
              value: convertSetpointTypedValue(plannedValue.value, newDataType.type),
            },
          ],
        }
      : {}),
  };
}

/**
 * Applies cached field types to settings that have a library parameter definition.
 * Returns the updated settings and any definition ids not found in the cache.
 */
export function applyFieldTypesFromCache(
  settings: Array<ProcedureDataParameterSetting>,
  cache: Record<string, DataParameterFieldType>
): {updatedSettings: Array<ProcedureDataParameterSetting>; missingIds: Array<string>} {
  const missingIdSet = new Set<string>();
  const updatedSettings = settings.map((setting) => {
    const libDef = setting.dataParameterVersion.libraryParameterDefinition;
    assert(
      BenchlingConfig.PROCEDURES_ENABLE_PARAMETER_LIBRARY_ENFORCEMENT,
      'Expected parameter library enforcement to be enabled'
    );
    // There must be a library parameter definition in order to submit the table. Just leave the setting alone
    // until a new library parameter definition is selected. This avoids showing errors in a transient state.
    if (libDef == null) {
      return setting;
    }
    const cachedFieldType = cache[libDef.id];
    if (cachedFieldType != null) {
      const newDataType = mergeLibraryFieldTypeWithExistingUnit(
        cachedFieldType,
        setting.dataParameterVersion.dataType
      );
      const withDataType = produce(setting, (draft) => {
        draft.dataParameterVersion.dataType = newDataType;
      });
      return reconcileTypeDependentFields(withDataType, newDataType);
    }
    missingIdSet.add(libDef.id);
    return setting;
  });
  return {updatedSettings, missingIds: [...missingIdSet]};
}
